import React, { useState } from 'react';
import './Help.css'  // FAQItem Component for reusability
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="bg-gray-50 rounded-xl overflow-hidden shadow-sm">
      <div
        className={`faq-accordion-header flex items-center justify-between p-4 font-bold text-lg text-gray-800 ${isOpen ? 'open' : ''}`}
        onClick={toggleOpen}
        aria-expanded={isOpen}
      >
        <span>{question}</span>
        <svg
          className="w-6 h-6 text-gray-600 transition-transform duration-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </div>
      <div className={`faq-accordion-content px-4 text-gray-700 ${isOpen ? 'open' : ''}`}>
        <p>{answer}</p>
      </div>
    </div>
  );
};

const HelpSection = () => {
  const faqs = [
    {
      question: 'How do I join a class?',
      answer: 'You can join an upcoming class directly from your "My Courses" page. Look for the "Join Class" button next to the scheduled class time.',
    },
    {
      question: 'How can I earn more points?',
      answer: 'Points can be earned by completing course modules, winning speech challenges, sharing on social media, and referring friends. Visit your "Rewards" page for a full list of actions!',
    },
    {
      question: 'Where can I find my class recordings?',
      answer: 'All class recordings and materials are available on your "My Courses" page under the specific course details. Look for the "View All Class Recordings & Materials" button.',
    },
    {
      question: 'How do I update my profile information?',
      answer: 'You can update your personal details, learning preferences, and contact information on your "My Profile" page. Just click the "Edit Profile" button.',
    },
  ];

  return (
    <main className="flex-grow container mx-auto p-4 md:p-8 lg:max-w-screen-xl xl:max-w-screen-2xl grid grid-cols-1 lg:grid-cols-3 gap-6 font-inter">
      {/* How Can We Help You Today? Section */}
      <div className="lg:col-span-3 content-card p-6 border-b-4 border-blue-500">
        <h2 className="text-2xl md:text-3xl font-extrabold text-gray-800 mb-4 flex items-center">
          <svg className="w-8 h-8 md:w-10 md:h-10 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          How Can We Help You Today?
        </h2>
        <p className="text-gray-600 mb-6">Find answers to common questions or reach out to our support team.</p>
        <div className="relative">
          <input type="text" placeholder="Search for answers..." className="input-field pr-10" />
          <button className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-blue-500 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          </button>
        </div>
      </div>

      {/* Frequently Asked Questions Section */}
      <div className="lg:col-span-2 content-card p-6 border-b-4 border-green-500">
        <h2 className="text-2xl md:text-3xl font-extrabold text-gray-800 mb-6 flex items-center">
          <svg className="w-8 h-8 md:w-10 md:h-10 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9.247a4.5 4.5 0 012.357-1.258c.884-.251 1.802-.251 2.686 0 .884.25 1.728.75 2.357 1.258m-4.714 0A10.5 10.5 0 0112 18.5c-4.478 0-8.268-2.943-9.548-7.253M12 7a4.5 4.5 0 00-4.714 4.714M12 7a4.5 4.5 0 014.714 4.714M12 7l-.293-.293m.293.293l.293.293M12 7V3m0 4a4.5 4.5 0 00-4.714 4.714m4.714-4.714a4.5 4.5 0 014.714 4.714m-4.714-4.714l-.293-.293m.293.293l.293.293M12 7v14"></path></svg>
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </div>

      {/* Contact Us Section */}
      <div className="lg:col-span-1 content-card p-6 border-b-4 border-orange-500">
        <h2 className="text-2xl md:text-3xl font-extrabold text-gray-800 mb-6 flex items-center">
          <svg className="w-8 h-8 md:w-10 md:h-10 mr-3 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-9 6h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          Contact Us
        </h2>
        <p className="text-gray-600 mb-4">Can't find what you're looking for? Send us a message!</p>
        <form className="space-y-4">
          <div>
            <label htmlFor="contact-name" className="block text-gray-700 font-semibold mb-2">Your Name:</label>
            <input type="text" id="contact-name" name="contact-name" className="input-field" placeholder="Enter your name" />
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-gray-700 font-semibold mb-2">Your Email:</label>
            <input type="email" id="contact-email" name="contact-email" className="input-field" placeholder="Enter your email" />
          </div>
          <div>
            <label htmlFor="contact-message" className="block text-gray-700 font-semibold mb-2">Your Message:</label>
            <textarea id="contact-message" name="contact-message" rows="5" className="input-field resize-y" placeholder="Type your message here..."></textarea>
          </div>
          <button type="submit" className="action-button w-full bg-orange-500 text-white rounded-full font-semibold hover:bg-orange-600">
            Send Message
          </button>
        </form>
      </div>

      {/* Quick Links Section */}
      <div className="lg:col-span-3 content-card p-6 border-b-4 border-purple-500">
        <h2 className="text-2xl md:text-3xl font-extrabold text-gray-800 mb-6 flex items-center">
          <svg className="w-8 h-8 md:w-10 md:h-10 mr-3 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.808a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m7.58-4.808a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101"></path></svg>
          Quick Links
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <a href="#" className="block bg-gray-50 p-4 rounded-xl text-center shadow-sm hover:bg-gray-100 transition-all duration-200 group">
            <span className="text-4xl block mb-2 group-hover:scale-110 transition-transform duration-200">📚</span>
            <p className="font-semibold text-gray-800">My Courses</p>
          </a>
          <a href="#" className="block bg-gray-50 p-4 rounded-xl text-center shadow-sm hover:bg-gray-100 transition-all duration-200 group">
            <span className="text-4xl block mb-2 group-hover:scale-110 transition-transform duration-200">🎁</span>
            <p className="font-semibold text-gray-800">My Rewards</p>
          </a>
          <a href="#" className="block bg-gray-50 p-4 rounded-xl text-center shadow-sm hover:bg-gray-100 transition-all duration-200 group">
            <span className="text-4xl block mb-2 group-hover:scale-110 transition-transform duration-200">👤</span>
            <p className="font-semibold text-gray-800">My Profile</p>
          </a>
          <a href="#" className="block bg-gray-50 p-4 rounded-xl text-center shadow-sm hover:bg-gray-100 transition-all duration-200 group">
            <span className="text-4xl block mb-2 group-hover:scale-110 transition-transform duration-200">📅</span>
            <p className="font-semibold text-gray-800">Calendar</p>
          </a>
          <a href="#" className="block bg-gray-50 p-4 rounded-xl text-center shadow-sm hover:bg-gray-100 transition-all duration-200 group">
            <span className="text-4xl block mb-2 group-hover:scale-110 transition-transform duration-200">💬</span>
            <p className="font-semibold text-gray-800">Messages</p>
          </a>
          <a href="#" className="block bg-gray-50 p-4 rounded-xl text-center shadow-sm hover:bg-gray-100 transition-all duration-200 group">
            <span className="text-4xl block mb-2 group-hover:scale-110 transition-transform duration-200">📖</span>
            <p className="font-semibold text-gray-800">Resources</p>
          </a>
        </div>
      </div>
    </main>
  );
};

export default HelpSection;